import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Box
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { API, graphqlOperation } from 'aws-amplify';
import { createGuidanceTypes, updateGuidanceTypes, deleteGuidanceTypes } from '../graphql/mutations'; // Assuming deleteGuidanceTypes mutation exists
import { listGuidanceTypes } from '../graphql/queries';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9',
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    backgroundColor: 'white',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

const AddGuidanceTypeForm = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [guidanceTypes, setGuidanceTypes] = useState([]);
  const [editingGuidanceTypeId, setEditingGuidanceTypeId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteGuidanceTypeId, setDeleteGuidanceTypeId] = useState(null);

  const classes = useStyles();


  useEffect(() => {
    fetchGuidanceTypes();
  }, []);

  const fetchGuidanceTypes = async () => {
    const response = await API.graphql(graphqlOperation(listGuidanceTypes));
    setGuidanceTypes(response.data.listGuidanceTypes.items);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (editingGuidanceTypeId) {
      // Update existing guidance type
      await API.graphql(
        graphqlOperation(updateGuidanceTypes, {
          input: {
            id: editingGuidanceTypeId,
            name: name,
            description: description,
            colour: color,
          },
        })
      );
    } else {
      // Create new guidance type
      await API.graphql(
        graphqlOperation(createGuidanceTypes, {
          input: {
            name: name,
            description: description,
            colour: color,
            deleted: 'False',
          },
        })
      );
    }

    // Clear form fields
    setName('');
    setDescription('');
    setColor('');
    setEditingGuidanceTypeId(null);

    fetchGuidanceTypes();
  };

  const handleEdit = (guidanceTypeId) => {
    const guidanceType = guidanceTypes.find((type) => type.id === guidanceTypeId);
    if (guidanceType) {
      setName(guidanceType.name);
      setDescription(guidanceType.description);
      setColor(guidanceType.colour);
      setEditingGuidanceTypeId(guidanceTypeId);
    }
  };

  const handleCancelEdit = () => {
    setName('');
    setDescription('');
    setColor('');
    setEditingGuidanceTypeId(null);
  };

  const handleColorChange = (event) => {
    setColor(event.target.value);
  };

  const handleDelete = async () => {
    await API.graphql(
      graphqlOperation(deleteGuidanceTypes, {
        input: { id: deleteGuidanceTypeId }
      })
    );
    setShowModal(false);
    fetchGuidanceTypes();
  };

  const openModal = (guidanceTypeId) => {
    setDeleteGuidanceTypeId(guidanceTypeId);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Name"
          variant="outlined"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Description"
          variant="outlined"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          fullWidth
          margin="normal"
        />
        <input
          type="color"
          value={color}
          onChange={handleColorChange}
          style={{ width: '100%', height: '56px', marginTop: '16px' }}
        />
        <br />
        <br />
        <Button type="submit" variant="contained" color="primary">
          {editingGuidanceTypeId ? 'Update' : 'Submit'}
        </Button>
        {editingGuidanceTypeId && (
          <Button variant="outlined" color="secondary" onClick={handleCancelEdit} style={{ marginLeft: '16px' }}>
            Cancel
          </Button>
        )}
      </form>
      <TableContainer component={Paper} className={classes.tableContainer} style={{ marginTop: '32px' }}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Color</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guidanceTypes.map((guidanceType) => (
              <TableRow key={guidanceType.id} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{guidanceType.name}</TableCell>
                <TableCell className={classes.tableCell}>{guidanceType.description}</TableCell>
                <TableCell className={classes.tableCell}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: guidanceType.colour,
                      borderRadius: '50%',
                    }}
                  ></div>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Button variant="outlined" color="primary" onClick={() => handleEdit(guidanceType.id)}>
                    Edit
                  </Button>
                  <Button variant="outlined" color="secondary" onClick={() => openModal(guidanceType.id)} style={{ marginLeft: '16px' }}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={showModal}
        onClose={closeModal}
      >
        <Box className={classes.modal}>
          <h2>Are you sure you want to delete this guidance type?</h2>
          <Button variant="contained" color="primary" onClick={handleDelete}>Yes</Button>
          <Button variant="outlined" color="secondary" onClick={closeModal} style={{ marginLeft: '16px' }}>No</Button>
        </Box>
      </Modal>

    </div>
  );
};

export default AddGuidanceTypeForm;
